import { useCallback, useState } from "react";

export type ViewMode = "list" | "grid";

export interface UseViewModeReturn {
  mode: ViewMode;
  setMode: (mode: ViewMode) => void;
  toggle: () => void;
  isGrid: boolean;
  isList: boolean;
}

function readStored(storageKey: string, defaultMode: ViewMode): ViewMode {
  if (typeof window === "undefined") return defaultMode;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw === "list" || raw === "grid") return raw;
  } catch {
    // ignore
  }
  return defaultMode;
}

export function useViewMode(
  pageKey: string,
  defaultMode: ViewMode = "list",
): UseViewModeReturn {
  const storageKey = `view_mode_${pageKey}`;
  const [mode, setModeState] = useState<ViewMode>(() =>
    readStored(storageKey, defaultMode),
  );

  const setMode = useCallback(
    (next: ViewMode) => {
      setModeState(next);
      try {
        window.localStorage.setItem(storageKey, next);
      } catch {
        // ignore
      }
    },
    [storageKey],
  );

  const toggle = useCallback(() => {
    setMode(mode === "list" ? "grid" : "list");
  }, [mode, setMode]);

  return {
    mode,
    setMode,
    toggle,
    isGrid: mode === "grid",
    isList: mode === "list",
  };
}
